import './2fa.scss';

import { complete2fa, disable2fa, enable2fa } from '@basqet-dashboard/common/api';
import { ReactComponent as Android } from '@basqet-dashboard/common/assets/icons/android.svg';
import { ReactComponent as Apple } from '@basqet-dashboard/common/assets/icons/apple.svg';
import { Button, FormInput, Modal } from '@basqet-dashboard/common/components';
import { APP_NAME } from '@basqet-dashboard/common/config';
import { useSessionStorage } from '@basqet-dashboard/common/hooks';
import { formatErrorMessage } from '@basqet-dashboard/common/utils';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

const TwoFactorAuthentication = () => {
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useSessionStorage(APP_NAME, '');
  const { TwoFaEnabled } = token;

  const remove2fa = async () => {
    setIsLoading(true);
    try {
      await disable2fa();
      toast.success('Two Factor Authentication has been disabled');
      setToken({ ...token, TwoFaEnabled: false });
    } catch (error) {
      const {
        response: {
          data: { message }
        }
      } = error;
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="card two-fa">
        <h3 className="card__header">2 Factor Authentication</h3>
        <p className="card__body">
          Two-Factor Authentication (2FA) can be used to help protect your account from unauthorized
          access by requiring you to to enter a security code when you sign in.
        </p>
        {TwoFaEnabled ? (
          <Button onClick={remove2fa} isLoading={isLoading}>
            Disable 2FA
          </Button>
        ) : (
          <Button onClick={() => setShowModal(true)}>Set up</Button>
        )}
      </div>
      {showModal && <TwoFactorAuthenticationModal onModalClose={() => setShowModal(false)} />}
    </>
  );
};

const TwoFactorAuthenticationModal = ({ onModalClose }) => {
  const [barcode, setBarcode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [inputError, setInputError] = useState('');
  const [code, setCode] = useState('');
  const [token, setToken] = useSessionStorage(APP_NAME, '');
  useEffect(async () => {
    try {
      const {
        data: { imageUrl }
      } = await enable2fa();

      setBarcode(imageUrl);
    } catch (error) {
      formatErrorMessage(error);
    }
  }, []);
  const handleChange = (e) => {
    if (e.target.value.length === 6) {
      setInputError('');
    }
    setCode(e.target.value);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code && code.length === 6) {
      setInputError('');
      setIsLoading(true);
      try {
        await complete2fa({ code: code });
        setIsLoading(false);
        setToken({ ...token, TwoFaEnabled: true });
        toast.success('Two fa enabled successfully');
        setTimeout(() => {
          onModalClose();
          window.location.reload();
        }, 1000);
      } catch (error) {
        setIsLoading(false);
        formatErrorMessage(error);
      }
    } else {
      setInputError('Please enter a valid auth code');
    }
  };
  return (
    <Modal onModalClose={onModalClose} header={{ name: 'Set up', cursive: 'authenticator' }}>
      <ul className="twofa__steps">
        <li>
          Get the <span className="text-primary">Authenticator App.</span>
        </li>
        <li>
          In the App, select <span className="text-primary">Set up account.</span>
        </li>
        <li>
          Select <span className="text-primary">Scan a QR Code</span>
        </li>
        <li>
          Enter the <span className="text-primary">verification code</span> shown in the
          authenticator app
        </li>
      </ul>
      <div className="twofa__download">
        <Button variant="outline" isFull>
          <Android /> Download Google Authenticator of Android
        </Button>
        <Button variant="outline" isFull>
          <Apple /> Download Google Authenticator of iOS
        </Button>
      </div>
      <div className="twofa__qr">
        <img src={barcode} alt="Two factor authentication qr code" />
      </div>
      <form onSubmit={handleSubmit}>
        <FormInput
          placeholder="Enter code"
          name="authCode"
          type="tel"
          error={inputError}
          register={{ onChange: handleChange }}
        />
        <br />
        <Button isFull type="submit" isLoading={isLoading}>
          Confirm
        </Button>
      </form>
    </Modal>
  );
};

TwoFactorAuthenticationModal.propTypes = {
  onModalClose: PropTypes.func
};
export { TwoFactorAuthentication, TwoFactorAuthenticationModal };
